import './copy-text-area.scss';

import { useCallback, useEffect, useState } from 'react';

import { copyToClipboardHandler } from '../../../data/utils/copy-to-clipboard-handler';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { CustomFormattedMessage } from '../../intl/custom-formatted-message';

type CopyTextAreaProps = {
  className?: string;
  text: string;
  disabled?: boolean;
  message?: JSX.Element | string;
  onCopy?: () => void;
};

export function CopyTextArea({
  className,
  text,
  disabled,
  message,
  onCopy,
}: CopyTextAreaProps) {
  const [showCallback, setShowCallback] = useState<boolean>(false);

  const [value, setValue] = useState(text);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const onHandleCopy = useCallback(async () => {
    const result = await copyToClipboardHandler(value);

    if (result) {
      setShowCallback(true);

      setTimeout(() => {
        setShowCallback(false);
      }, 2000);

      onCopy?.();
    }
  }, [value, onCopy]);

  const onHideCallback = useCallback(() => {
    setShowCallback(false);
  }, []);

  useEffect(() => {
    document.addEventListener('click', onHideCallback);

    return () => {
      document.removeEventListener('click', onHideCallback);
    };
  }, [onHideCallback]);

  return (
    <span
      className={`copy-text-area${
        className ? ` copy-text-area--${className}` : ''
      }${showCallback ? ' active' : ''}`}
    >
      <textarea
        className="copy-text-area__content"
        value={value}
        disabled={disabled}
        onChange={e => {
          setValue(e.currentTarget.value);
        }}
      />
      <span
        className="copy-text-area-icon"
        role="button"
        tabIndex={0}
        onClick={onHandleCopy}
        onKeyDown={makeKeyDownHandler(onHandleCopy)}
      />
      {showCallback && (
        <span className="copy-text-area-message">
          {message ?? (
            <CustomFormattedMessage id="common.copied-to-clipboard" />
          )}
        </span>
      )}
    </span>
  );
}
